import { useState } from 'react'
import { useQuery, useAction } from 'convex/react'
import { Link } from 'react-router-dom'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { Loader2, ExternalLink, Play, RefreshCw, AlertCircle, CheckCircle2, Clock } from 'lucide-react'

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
] as const

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-neutral-100 text-neutral-600',
  running: 'bg-amber-50 text-amber-700',
  completed: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
  skipped: 'bg-neutral-50 text-neutral-400',
}

function formatTime(ts?: number) {
  if (!ts) return '—'
  const diff = Date.now() - ts
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hrs = Math.floor(min / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatCents(cents?: number) {
  if (cents == null) return '—'
  return `$${(cents / 100).toFixed(2)}`
}

export default function AdminAnalysisQueuePage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const [filter, setFilter] = useState<string>('all')
  const [running, setRunning] = useState(false)
  const [status, setStatus] = useState<string | null>(null)

  const rows = useQuery(api.analysisQueueDb.adminListQueueRows, isAdmin ? { statusFilter: filter, limit: 200 } : 'skip')
  const budgets = useQuery(api.analysisQueueDb.adminListBudgets, isAdmin ? { days: 14 } : 'skip')
  const triggerSweep = useAction(api.analysisQueueDb.adminTriggerDailySweep)
  const triggerMeta = useAction(api.analysisQueueDb.adminTriggerMeta)

  if (!isAdmin) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">Admin access required.</div>
  }

  const today = new Date().toISOString().slice(0, 10)
  const todayBudget = budgets?.find((b: any) => b.date === today)

  const handleRunSweep = async () => {
    setRunning(true)
    setStatus('Running sweep…')
    try {
      const result: any = await triggerSweep({ maxDispatch: 50 })
      setStatus(`Sweep dispatched ${result.dispatched}/${result.enqueued} (${result.skippedBudget} blocked by budget)`)
    } catch (e: any) {
      setStatus(`Error: ${e?.message || e}`)
    } finally {
      setRunning(false)
    }
  }

  const handleRunMeta = async () => {
    setRunning(true)
    setStatus('Triggering meta analysis (will run async — Phase 1 → 2 → 3)…')
    try {
      const result: any = await triggerMeta({})
      setStatus(`Meta started: ${result.numCandidates} candidates, runId ${result.runId ?? '(none)'}`)
    } catch (e: any) {
      setStatus(`Error: ${e?.message || e}`)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="mx-auto max-w-6xl px-6 py-6 space-y-5">
        <header className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Analysis Queue</h1>
            <p className="text-sm text-neutral-500 mt-1">
              Daily 3am UTC sweep enqueues new sessions; 4am UTC meta-cluster groups them. $100/day cap.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRunSweep}
              disabled={running}
              className="text-sm px-3 py-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Play className="h-3.5 w-3.5" /> Run sweep now
            </button>
            <button
              onClick={handleRunMeta}
              disabled={running}
              className="text-sm px-3 py-1.5 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Run meta now
            </button>
          </div>
        </header>

        {status && (
          <div className="text-xs text-neutral-600 bg-white border border-neutral-200 rounded-md px-3 py-2">{status}</div>
        )}

        {/* Today's spend */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat
            label="Today's spend"
            value={formatCents(todayBudget?.spentCents)}
            sub={`cap ${formatCents(todayBudget?.capCents ?? 10000)}`}
          />
          <Stat
            label="Sessions today"
            value={String(todayBudget?.sessionCount ?? 0)}
            sub={todayBudget?.metaCount ? `${todayBudget.metaCount} meta runs` : '0 meta runs'}
          />
          <Stat
            label="In flight"
            value={rows ? String(rows.filter((r: any) => r.status === 'running' || r.status === 'pending').length) : '—'}
            sub={rows ? `${rows.length} recent rows` : ''}
          />
        </section>

        {/* Recent budgets */}
        {budgets && budgets.length > 0 && (
          <section>
            <h2 className="text-xs uppercase tracking-wide text-neutral-400 mb-2">Last 14 days spend</h2>
            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 text-neutral-500">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium">Date</th>
                    <th className="text-right px-3 py-1.5 font-medium">Spent</th>
                    <th className="text-right px-3 py-1.5 font-medium">Cap</th>
                    <th className="text-right px-3 py-1.5 font-medium">Sessions</th>
                    <th className="text-right px-3 py-1.5 font-medium">Meta runs</th>
                  </tr>
                </thead>
                <tbody>
                  {budgets.map((b: any) => (
                    <tr key={b._id} className="border-t border-neutral-100">
                      <td className="px-3 py-1.5 font-mono">{b.date}</td>
                      <td className="px-3 py-1.5 text-right">{formatCents(b.spentCents)}</td>
                      <td className="px-3 py-1.5 text-right text-neutral-400">{formatCents(b.capCents)}</td>
                      <td className="px-3 py-1.5 text-right">{b.sessionCount}</td>
                      <td className="px-3 py-1.5 text-right">{b.metaCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Status filter */}
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-neutral-400">Status</span>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.value}
              onClick={() => setFilter(s.value)}
              className={`text-xs px-2 py-0.5 rounded border transition-colors ${
                filter === s.value
                  ? 'bg-neutral-900 text-white border-neutral-900'
                  : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Queue rows */}
        <section>
          {!rows ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-sm text-neutral-400 py-12">No queue rows match this filter.</div>
          ) : (
            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 text-neutral-500">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium">Status</th>
                    <th className="text-left px-3 py-1.5 font-medium">Workflow</th>
                    <th className="text-left px-3 py-1.5 font-medium">Trigger</th>
                    <th className="text-right px-3 py-1.5 font-medium">Cost</th>
                    <th className="text-right px-3 py-1.5 font-medium">Enqueued</th>
                    <th className="text-right px-3 py-1.5 font-medium">Finished</th>
                    <th className="text-left px-3 py-1.5 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: any) => (
                    <tr key={r._id} className="border-t border-neutral-100">
                      <td className="px-3 py-1.5">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${STATUS_STYLE[r.status] || 'bg-neutral-100'}`}>
                          {r.status === 'running' && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
                          {r.status === 'completed' && <CheckCircle2 className="h-2.5 w-2.5" />}
                          {r.status === 'failed' && <AlertCircle className="h-2.5 w-2.5" />}
                          {r.status === 'pending' && <Clock className="h-2.5 w-2.5" />}
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-1.5">
                        <Link
                          to={`/admin/projects/detail/${r.workflowId}`}
                          className="text-blue-600 hover:underline inline-flex items-center gap-0.5 font-mono"
                        >
                          {String(r.workflowId).slice(0, 12)}…
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </td>
                      <td className="px-3 py-1.5 text-neutral-500">{r.trigger}</td>
                      <td className="px-3 py-1.5 text-right">{formatCents(r.estimatedCostCents)}</td>
                      <td className="px-3 py-1.5 text-right text-neutral-500">{formatTime(r.enqueuedAt)}</td>
                      <td className="px-3 py-1.5 text-right text-neutral-500">{formatTime(r.finishedAt)}</td>
                      <td className="px-3 py-1.5 text-red-600 max-w-[200px] truncate" title={r.errorMessage}>
                        {r.errorMessage || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-md px-4 py-3">
      <div className="text-[10px] uppercase tracking-wide text-neutral-400">{label}</div>
      <div className="text-2xl font-semibold text-neutral-900 mt-1">{value}</div>
      {sub && <div className="text-[11px] text-neutral-400 mt-0.5">{sub}</div>}
    </div>
  )
}
